import { useTranslation } from "react-i18next";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { useApiQuery } from "../../../services/api";
import { useTrackOnMount } from "../../../hooks/use-track-on-mount";
import { gaEventViewItemListGptAnswerSources } from "../../../tools/analytics/domains/article.analytics";
import {
  assistantChatSource,
  assistantSourcesRefetchInterval,
} from "../../../domains/gpt/chatSource";
import { ChatSources, ChatSourcesSkeleton } from "../../ui/ChatSources";
import { useSelectedGptSource } from "./GPTSourceSheet";

/**
 * The Sources tab of an assistant answer: the sources it cited, under the
 * numbers its chips show, then the other sources the conversation had captured
 * by the time it answered.
 *
 * The api resolves external sources in the background, so the list reads
 * itself again until each one is resolved.
 */
export default function AssistantSourcesList({
  conversationId,
  runId,
  size,
  roomId,
}: {
  conversationId: string;
  runId: string;
  // `row` in the Sources tab, `rail` in the desktop context panel.
  size: "row" | "rail";
  roomId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { articleLink } = usePageLinks(roomId);
  const [, setSelected] = useSelectedGptSource();
  const query = useApiQuery(
    "/assistant/conversations/:conversationId/runs/:runId/sources",
    {
      param: { conversationId, runId },
      refetchInterval: assistantSourcesRefetchInterval,
    }
  );
  const articleIds =
    query.state === "success"
      ? [
          ...new Set(
            [...query.body.cited, ...query.body.consulted].flatMap((row) =>
              row.source.kind === "article" ? [row.source.article.id] : []
            )
          ),
        ]
      : [];
  const articles = useApiQuery(
    "/webapp/public/articles/batch",
    articleIds.length === 0
      ? false
      : { query: { ids: articleIds.join(",") }, durable: true }
  );
  const shownArticles = articles.state === "success" ? articles.body : [];
  const articleById = new Map(
    shownArticles.map((article) => [article.id, article])
  );
  // The list view the legacy Sources tab counts, here once per run and session.
  useTrackOnMount(
    "gpt-answer-sources-shown",
    shownArticles.length === 0
      ? { status: "hidden" }
      : { status: "shown", key: runId },
    () => {
      gaEventViewItemListGptAnswerSources({
        articles: shownArticles,
        engine: "assistant",
      });
    }
  );

  if (query.state === "pending") return <ChatSourcesSkeleton size={size} />;
  if (query.state === "error")
    return (
      <p className="m-0 py-8 text-center font-dm-sans text-footnote text-ds-slate-600">
        {t("Couldn't load the sources.")}
      </p>
    );

  const { cited, consulted } = query.body;
  // The list's own empty state says the answer rests on nothing.
  if (cited.length === 0 && consulted.length === 0)
    return (
      <ChatSources
        rows={[]}
        size={size}
        onSelect={() => {
          throw new Error("should not happen: an empty list has no row");
        }}
      />
    );

  return (
    <div className="flex flex-col gap-6">
      {/* In the desktop rail the numbered list needs no title: its numbers
          are the answer's citations (Figma
          "D2.conversation.reponse.progression"). The phone's Sources tab keeps
          its two counted headings. */}
      {cited.length > 0 && (
        <section
          aria-label={t("Cited in the answer")}
          className="flex flex-col gap-3"
        >
          {size === "rail" ? null : (
            <h3 className="m-0 font-dm-sans text-footnote font-semibold text-ds-slate-600">
              {`${t("Cited in the answer")} (${String(cited.length)})`}
            </h3>
          )}
          <ChatSources
            size={size}
            rows={cited.map((row) => ({
              source: assistantChatSource(row.source, articleById, articleLink),
              number: row.number,
            }))}
            onSelect={(index) => {
              const row = cited.at(index);
              if (row === undefined)
                throw new Error(`should not happen: no cited row ${index}`);
              setSelected({
                kind: "assistant",
                conversationId,
                runId,
                target: { kind: "cited", number: row.number },
              });
            }}
          />
        </section>
      )}
      {consulted.length > 0 && (
        <section className="flex flex-col gap-3">
          {size === "rail" ? (
            <h3 className="m-0 font-dm-sans text-xs font-bold text-ds-slate-950">
              {t("Consulted, not cited")}
            </h3>
          ) : (
            <h3 className="m-0 font-dm-sans text-footnote font-semibold text-ds-slate-600">
              {`${t("Also consulted")} (${String(consulted.length)})`}
            </h3>
          )}
          <ChatSources
            size={size}
            rows={consulted.map((row) => ({
              source: assistantChatSource(row.source, articleById, articleLink),
              number: null,
            }))}
            onSelect={(index) => {
              const row = consulted.at(index);
              if (row === undefined)
                throw new Error(`should not happen: no consulted row ${index}`);
              setSelected({
                kind: "assistant",
                conversationId,
                runId,
                target: {
                  kind: "consulted",
                  evidenceId: row.source.evidenceId,
                },
              });
            }}
          />
        </section>
      )}
    </div>
  );
}
